/**
 * AIDocumentUpload — reusable component for uploading a document
 * for AI-assisted field extraction. Supports drag-and-drop, file
 * type validation, progress indication, and error handling.
 *
 * Requirements: 19.1, 19.2, 19.3, 19.4
 */

import { useState, useRef, useCallback, type DragEvent, type ChangeEvent } from 'react';
import {
  uploadContractForExtraction,
  uploadInvoiceForExtraction,
  isSupportedFileType,
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_FILE_DESCRIPTION,
  type ContractSetupResult,
  type InvoiceSetupResult,
} from '@/services/aiSetupService';

export interface AIDocumentUploadProps {
  /** Called when extraction completes successfully. */
  onExtracted: (result: ContractSetupResult | InvoiceSetupResult) => void;
  /** The type of document being uploaded. */
  documentType: 'contract' | 'invoice';
  /** Whether the upload control is disabled. */
  disabled?: boolean;
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

export function AIDocumentUpload({
  onExtracted,
  documentType,
  disabled = false,
}: AIDocumentUploadProps) {
  const [state, setState] = useState<UploadState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (!isSupportedFileType(file.type)) {
        setErrorMessage(
          `Unsupported file type. Please upload a ${ACCEPTED_FILE_DESCRIPTION} file.`,
        );
        setState('error');
        return;
      }

      setFileName(file.name);
      setState('uploading');
      setErrorMessage(null);

      try {
        const result =
          documentType === 'contract'
            ? await uploadContractForExtraction(file)
            : await uploadInvoiceForExtraction(file);

        setState('success');
        onExtracted(result);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred during extraction.';
        setErrorMessage(message);
        setState('error');
      }
    },
    [documentType, onExtracted],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      // Reset input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handleFile],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (disabled || state === 'uploading') return;
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [disabled, state, handleFile],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && state !== 'uploading') setIsDragOver(true);
    },
    [disabled, state],
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleRetry = useCallback(() => {
    setState('idle');
    setErrorMessage(null);
    setFileName(null);
  }, []);

  const label =
    documentType === 'contract'
      ? 'Upload Contract for AI Extraction'
      : 'Upload Invoice for AI Extraction';

  return (
    <div
      data-testid="ai-document-upload"
      style={{
        marginBottom: '1.5rem',
        padding: '1rem',
        borderRadius: '8px',
        border: '2px dashed',
        borderColor: isDragOver
          ? '#c8102e'
          : state === 'error'
            ? '#ef4444'
            : state === 'success'
              ? '#22c55e'
              : '#d1d5db',
        backgroundColor: isDragOver
          ? '#eff6ff'
          : state === 'error'
            ? '#fef2f2'
            : state === 'success'
              ? '#f0fdf4'
              : '#f8fafc',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '1.1rem' }}>🤖</span>
        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>
          {label}
        </span>
      </div>

      {state === 'idle' && (
        <>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#64748b' }}>
            Drag and drop a document here, or click to browse. AI will extract fields
            automatically. Accepted formats: {ACCEPTED_FILE_DESCRIPTION}.
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{
              padding: '0.4rem 1rem',
              borderRadius: '6px',
              border: '1px solid #c8102e',
              backgroundColor: '#eff6ff',
              color: '#c8102e',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
          >
            Choose File
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_EXTENSIONS}
            onChange={handleInputChange}
            style={{ display: 'none' }}
            aria-label={label}
          />
        </>
      )}

      {state === 'uploading' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div
            style={{
              width: '20px',
              height: '20px',
              border: '3px solid #e2e8f0',
              borderTopColor: '#c8102e',
              borderRadius: '50%',
              animation: 'ai-upload-spin 0.8s linear infinite',
            }}
            role="status"
            aria-label="Processing document"
          />
          <div>
            <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 500, color: '#1e293b' }}>
              Processing {fileName}…
            </p>
            <p style={{ margin: '0.15rem 0 0', fontSize: '0.75rem', color: '#64748b' }}>
              AI is extracting fields from your document. This may take 10–30 seconds.
            </p>
          </div>
          <style>{`@keyframes ai-upload-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {state === 'success' && (
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#16a34a', fontWeight: 500 }}>
          ✓ Fields extracted from {fileName}. Review the pre-populated values below.
        </p>
      )}

      {state === 'error' && (
        <div>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#dc2626', fontWeight: 500 }}>
            ✗ {errorMessage}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={handleRetry}
              style={{
                padding: '0.3rem 0.75rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '0.75rem',
              }}
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={() => setState('idle')}
              style={{
                padding: '0.3rem 0.75rem',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'transparent',
                color: '#64748b',
                cursor: 'pointer',
                fontSize: '0.75rem',
              }}
            >
              Continue with Manual Entry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
